import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { Button, IconButton, Stack, Typography, FormControlLabel, FormLabel, FormGroup, Switch, Paper, Box, Chip } from '@mui/material';
import { Delete, Edit, NotificationImportantRounded } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { updateWorkflow } from '../../../app/store/workflowSlice';
import { acceptAssessment, rejectAssessment } from '../../../services/workflowThunks';
import { deleteAssessment } from '../../../app/store/assessmentSlice';
import { SiteCloudStatus, Survey } from '../../../interfaces/survey.interface';
import { Workflow } from '../../../interfaces/workflow.interface';
import User, { Company } from '../../../interfaces/users.interface';
import { Assessment, AssessmentSurvey } from '../../../interfaces/assessment.interface';
import {
  fetchAssessment,
  getSiteCloudStatus,
  updateAssessmentSurvey,
} from '../../../services/assessments';
import WorkflowStatus from '../WorkflowStatus';
import AssessmentSurveyRow from './AssessmentSurveyRow';
import AssessmentForm from '../../Assessments/NewAssessmentModal';
import AssessorAssessmentComments from '../../Comments/AssessorAssessmentComments';
import { DataCell, DataRow, DataRowActions } from '../../Tables/DataList';
import { tightTableSx } from '../../Tables/dataTableStyles';
import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { USER_TYPES } from '../../../services/user';
import { COMPANY_TYPES } from '../../../services/vendor';
import { WorkflowTypeHierarchy } from '../../../interfaces/workflow.interface';
import moment from 'moment';

type AssessmentCardProps = {
  company: Company;
  workflow: Workflow;
  vendorCompany?: Company;
  userCanAdminCompany: boolean;
};

const AssessmentCard = (props: AssessmentCardProps) => {
  const theme = useTheme();
  const { userCanAdminCompany, company, workflow, vendorCompany } = props;
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { assessmentAuditLog } = useAppSelector((state) => state.assessments);

  const [showAlert, setShowAlert] = useState(false);
  const [cAssessment, setcAssessment] = useState<Assessment | undefined>(undefined);
  const [surveys, setFilteredSurveys] = useState<Survey[]>([]);
  const [assessedCompany, setAssessedCompany] = useState<Company | undefined>(undefined);

  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [deleteAssessmentOpen, setDeleteAssessmentOpen] = useState(false);
  const [acceptAssessmentOpen, setAcceptAssessmentOpen] = useState(false);
  const [rejectAssessmentOpen, setRejectAssessmentOpen] = useState(false);

  const currSurvey = useMemo(() => surveys?.find((sur) => sur.id === workflow.bpSurvey), [surveys, workflow.bpSurvey]);
  const currAssSurvey = useMemo(
    () => (cAssessment?.surveys as AssessmentSurvey[])?.find((assSur) => assSur?.id === workflow?.assessmentSurvey),
    [cAssessment?.surveys, workflow.assessmentSurvey],
  );

  const handleAcceptAssessmentOpen = useCallback(() => setAcceptAssessmentOpen(true), []);
  const handleAcceptAssessmentClose = useCallback(() => setAcceptAssessmentOpen(false), []);
  const handleRejectAssessmentOpen = useCallback(() => setRejectAssessmentOpen(true), []);
  const handleRejectAssessmentClose = useCallback(() => setRejectAssessmentOpen(false), []);
  const handleDeleteAssessmentOpen = useCallback(() => setDeleteAssessmentOpen(true), []);
  const handleDeleteAssessmentClose = useCallback(() => setDeleteAssessmentOpen(false), []);

  const handleAssessmentDeleteSubmit = useCallback(
    (assessmentId: number) => {
      dispatch(deleteAssessment(assessmentId));
      if (workflow) dispatch(updateWorkflow({ adminOverride: true, id: workflow.id, assessment: null, assessmentSurvey: null, assessor: null, assignedAssessor: null, status: 'submittedForAssessment' }));
      handleDeleteAssessmentClose();
    },
    [dispatch],
  );

  const handleAcceptAssessment = useCallback(
    async () => {
      try {
        const result = await dispatch(acceptAssessment(workflow.id)).unwrap();
        if (result.assessment) {
          setcAssessment(result.assessment);
        }
      } catch (error) {
        console.error('Failed to accept assessment:', error);
      }
      handleAcceptAssessmentClose();
    },
    [workflow.id, dispatch],
  );

  const handleRejectAssessment = useCallback(
    async () => {
      try {
        await dispatch(rejectAssessment(workflow.id)).unwrap();
        setcAssessment(undefined);
      } catch (error) {
        console.error('Failed to reject assessment:', error);
      }
      handleRejectAssessmentClose();
    },
    [workflow.id, dispatch],
  );

  const updateAssessmentFromAssessmentSurvey = useCallback(
    (assessmentSurvey: AssessmentSurvey) => {
      const assessmentSurveyIndex = (cAssessment?.surveys as AssessmentSurvey[]).findIndex(
        (assSur) => assSur.id === assessmentSurvey.id,
      );
      if (assessmentSurveyIndex === -1) return;
      const newAssessment = JSON.parse(JSON.stringify(cAssessment));
      newAssessment.surveys[assessmentSurveyIndex] = assessmentSurvey;
      setcAssessment(newAssessment);
    },
    [cAssessment],
  );

  const surveyRow = useMemo(
    () => (assessmentSurvey: AssessmentSurvey) => {
      if (!assessmentSurvey || (company.type === COMPANY_TYPES.ASSESSOR && !assessedCompany) || !surveys) return;
      const theCompany = assessedCompany ? assessedCompany : company;

      const survey = surveys.find((sur) =>
        typeof assessmentSurvey.survey === 'number'
          ? sur.id === assessmentSurvey.survey
          : sur.id === assessmentSurvey.survey.id!,
      );
      if (!survey) return;

      // Site + company application are embedded on the survey by the assessment
      // fetch, so no company-wide sites/apps lookup is needed.
      const location = survey.siteDetail ?? undefined;
      const app = survey.companyApplicationDetail ?? undefined;

      return (
        <AssessmentSurveyRow
          key={assessmentSurvey.id}
          companyApplication={app}
          site={location}
          company={theCompany}
          assessment={cAssessment!}
          survey={survey}
          assessmentSurvey={assessmentSurvey}
          updateAssessmentFromAssessmentSurvey={updateAssessmentFromAssessmentSurvey}
        />
      );
    },
    [company, assessedCompany, surveys],
  );

  const [showAssessmentComments, setShowAssessmentComments] = useState(false);
  const [assessmentComment, setAssessmentComment] = useState<string>('');
  useEffect(() => {
    if (currAssSurvey?.assessorComment) {
      setAssessmentComment(currAssSurvey.assessorComment);
    }
  }, [currAssSurvey?.assessorComment]);

  const handleSetShowAssessmentComments = useCallback((comment: string) => {
    setAssessmentComment(comment);
    setShowAssessmentComments(true);
  }, [setAssessmentComment, setShowAssessmentComments]);

  const handleAssessmentComment = useCallback(async (assessorComment: string) => {
    if (currAssSurvey !== undefined) {
      try {
        await updateAssessmentSurvey({ ...currAssSurvey, assessorComment });
        setAssessmentComment(assessorComment);
      } catch (e) {
        console.error('failed to update assessmentSurvey');
      }
    }
  }, [currAssSurvey]);

  useEffect(() => {
    if (workflow?.assessment) {
      fetchAssessment(workflow.assessment!).then((newAssessment) => setcAssessment(newAssessment));
    }
  }, [workflow.assessment]);

  useEffect(() => {
    const filteredSurveys = (cAssessment?.surveys as AssessmentSurvey[])?.map((sur) => sur.survey as Survey);
    setFilteredSurveys(filteredSurveys ? filteredSurveys.filter(sur => sur) : []);
  }, [cAssessment]);

  useEffect(() => {
    setAssessedCompany(vendorCompany);
  }, [vendorCompany]);

  useEffect(() => {
    if (!cAssessment?.surveys.length) return;
    if (!assessmentAuditLog.length) {
      setShowAlert(false);
      return;
    }
    const curAssessmentSurveys = (cAssessment?.surveys as AssessmentSurvey[]).filter(
      (assSur) => ['Complete', 'Remediation'].indexOf(assSur.status) === -1,
    );
    const assessmetnSurveyIds = curAssessmentSurveys.map((assSur) => assSur.id);
    setShowAlert(
      assessmentAuditLog.find(
        (assAl) =>
          assessmetnSurveyIds.indexOf(assAl.assessmentSurvey) > -1 &&
          assAl.auditLogs.filter((al) => (al.user as User).id !== user?.id).length,
      ) !== undefined,
    );
  }, [assessmentAuditLog, cAssessment]);

  const [siteCloudStatus, setSiteCloudStatus] = useState<SiteCloudStatus>({ site: false, cloud: false, both: false });

  const fetchSiteCloudStatus = async (baselineId: number) => {
    const siteCloundData = await getSiteCloudStatus(baselineId);
    setSiteCloudStatus(siteCloundData);
  };

  useEffect(() => {
    if (workflow?.baseline) fetchSiteCloudStatus(workflow.baseline);
  }, [workflow?.baseline]);

  if (!cAssessment) {
    return null;
  }

  const commonTypographyStyles = {
    marginBottom: '15px',
    color: theme.palette.customThemeColors?.darkGrey,
    fontSize: '14px',
  };

  const partnerName = company.type === COMPANY_TYPES.VENDOR
    ? (cAssessment?.assessor as Company)?.name
    : (cAssessment?.company as Company)?.name;

  const canAdminAssessment = (userCanAdminCompany && user?.type === USER_TYPES.VENDOR_ADMIN && ['Assigned', 'Rejected'].indexOf(cAssessment?.status!) !== -1) || user?.type === USER_TYPES.TPN_ADMIN;
  const canShowAssessorSummary = ['Assigned', 'Rejected'].indexOf(cAssessment?.status!) === -1;

  // Build a unique identifier for this assessment
  const workflowVersion = workflow.workflowType && WorkflowTypeHierarchy[workflow.workflowType]
    ? WorkflowTypeHierarchy[workflow.workflowType]
    : null;
  const assessmentId = cAssessment?.id;
  const createdAt = cAssessment?.createdAt ? moment(cAssessment.createdAt).format('MM/DD/YYYY') : null;
  const dateAccepted = cAssessment?.dateAccepted ? moment(cAssessment.dateAccepted).format('MM/DD/YYYY') : null;

  return (
    <>
      <AssessorAssessmentComments
        canEdit={user && user.type === COMPANY_TYPES.ASSESSOR && currAssSurvey && ['Complete', 'Remediation'].indexOf(currAssSurvey.status) === -1}
        originalComment={assessmentComment}
        handleAssessmentComment={handleAssessmentComment}
        setShowAssessmentComments={setShowAssessmentComments}
        showAssessmentComments={showAssessmentComments}
      />

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Identification Banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.25,
            backgroundColor: '#EEF3FB',
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 2,
          }}
        >
          <Stack direction='row' spacing={1.5} alignItems='center' divider={<Box sx={{ width: '1px', height: '14px', backgroundColor: '#C5D2E5' }} />}>
            {workflowVersion && (
              <Typography variant='body2' sx={{ fontWeight: 700, color: '#1976d2', letterSpacing: '0.02em' }}>
                TPN Best Practices v{workflowVersion}
              </Typography>
            )}
            {assessmentId && (
              <Typography variant='body2' color='text.secondary'>
                Assessment #{assessmentId}
              </Typography>
            )}
            {createdAt && (
              <Typography variant='body2' color='text.secondary'>
                Created {createdAt}
              </Typography>
            )}
            {dateAccepted && (
              <Typography variant='body2' color='text.secondary'>
                Accepted {dateAccepted}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Card Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            p: 2.5,
            backgroundColor: '#FAFBFC',
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 2,
          }}
        >
          {/* Partner Info */}
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {showAlert && (
                <NotificationImportantRounded color='warning' sx={{ fontSize: '1.1em' }} />
              )}
              <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                {company.type === COMPANY_TYPES.VENDOR ? 'Assessor' : 'Company'}
              </Typography>
            </Box>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              {partnerName}
            </Typography>
            <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${cAssessment?.surveys.length} Questionnaire${cAssessment?.surveys.length !== 1 ? 's' : ''}`}
                size='small'
                variant='outlined'
              />
            </Stack>
          </Box>

          {/* Status */}
          <Box sx={{ flex: '0 0 auto', minWidth: 180, textAlign: 'left' }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
              Status
            </Typography>
            <WorkflowStatus
              company={company}
              workflow={workflow}
              survey={currSurvey}
              assessmentSurvey={currAssSurvey}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
            {company.type === COMPANY_TYPES.VENDOR && userCanAdminCompany && cAssessment?.status === 'Rejected' && (
              <Button
                variant='outlined'
                size='small'
                sx={{ minWidth: 150 }}
                onClick={() => setAssessmentModalOpen(true)}
              >
                Reassign
              </Button>
            )}
            {canShowAssessorSummary && (
              <Button
                size='small'
                variant='outlined'
                sx={{ minWidth: 180 }}
                onClick={(e) => { e.stopPropagation(); handleSetShowAssessmentComments(assessmentComment ?? currAssSurvey?.assessorComment); }}
              >
                Assessor Summary
              </Button>
            )}
            {company.type === COMPANY_TYPES.ASSESSOR && cAssessment?.status === 'Assigned' && (
              <Stack direction='row' spacing={1}>
                <Button
                  variant='outlined'
                  size='small'
                  sx={{ minWidth: 100 }}
                  onClick={handleAcceptAssessmentOpen}
                >
                  Accept
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  color='error'
                  sx={{ minWidth: 100 }}
                  onClick={handleRejectAssessmentOpen}
                >
                  Reject
                </Button>
              </Stack>
            )}
            {canAdminAssessment && (
              <DataRowActions>
                <IconButton size='small' name='Delete Assessment' onClick={handleDeleteAssessmentOpen}>
                  <Delete fontSize='small' />
                </IconButton>
                {['Assigned', 'Rejected'].indexOf(cAssessment?.status!) !== -1 && (
                  <IconButton size='small' name='Edit Assessment' onClick={() => setAssessmentModalOpen(true)}>
                    <Edit fontSize='small' />
                  </IconButton>
                )}
              </DataRowActions>
            )}
          </Box>
        </Box>

        {/* Surveys Section */}
        {cAssessment?.surveys.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', display: 'block', mb: 1, pl: 1 }}>
              Questionnaires
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', ...tightTableSx }}>
              <DataRow header>
                <DataCell xs={2}>Type</DataCell>
                <DataCell xs={2}>Name</DataCell>
                <DataCell xs={2}>Scope</DataCell>
                <DataCell xs={2}>Type</DataCell>
                <DataCell xs={2}>Status</DataCell>
                <DataCell xs={2} textAlign='center'>Actions</DataCell>
              </DataRow>
              {(cAssessment?.surveys as AssessmentSurvey[]).map((survey) => surveyRow(survey))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Modals */}
      <StandardDialog
        title={'Delete Assessment?'}
        handleClose={handleDeleteAssessmentClose}
        isOpen={deleteAssessmentOpen}
      >
        <Typography>Are you sure you want to delete this assessment? Please notify the assessor after deleting.</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={handleDeleteAssessmentClose}>Cancel</Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<Delete />}
            onClick={() => handleAssessmentDeleteSubmit(cAssessment?.id as number)}
            disabled={!userCanAdminCompany}
          >
            Delete
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      <StandardDialog
        title={'Accept Assessment?'}
        handleClose={handleAcceptAssessmentClose}
        isOpen={acceptAssessmentOpen}
      >
        <Typography sx={{ ...commonTypographyStyles, fontSize: '16px' }}>
          Are you sure you want to accept this assessment?
        </Typography>
        <Typography sx={commonTypographyStyles}>
          By clicking &quot;ACCEPT&quot; you confirm that you have reviewed the Scoping Baseline and Questionnaire answers.
        </Typography>
        <Typography sx={commonTypographyStyles}>
          Please remind the Service Provider to make any needed changes after you &quot;Accept&quot; their assessment, before you click on &quot;Begin Assessment&quot;. This will be the only opportunity for the Service Providers to update their questionnaire answers.
        </Typography>
        <Typography sx={commonTypographyStyles}>
          Please note that additional questions may be exposed or hidden as part of the adjustment process.
        </Typography>
        <Stack spacing={2} justifyContent="flex-start" alignItems="flex-start">
          <FormGroup sx={{ marginLeft: '7px' }}>
            <FormLabel component="legend">Scope</FormLabel>
            <FormControlLabel
              labelPlacement="end"
              disabled
              control={<Switch checked={siteCloudStatus.site || siteCloudStatus.both} />}
              label={
                <Typography sx={{ ...commonTypographyStyles, marginBottom: 0 }}>
                  {surveys[0]?.site ? 'Site' : 'On-Prem'}
                </Typography>
              }
            />
            <FormControlLabel
              labelPlacement="end"
              disabled
              control={<Switch checked={siteCloudStatus.cloud || siteCloudStatus.both} />}
              label={
                <Typography sx={{ ...commonTypographyStyles, marginBottom: 0 }}>
                  Cloud
                </Typography>
              }
            />
          </FormGroup>
        </Stack>
        <StandardDialogActions>
          <Button variant='outlined' onClick={handleAcceptAssessmentClose}>Cancel</Button>
          <Button variant='contained' color='primary' onClick={handleAcceptAssessment}>Accept</Button>
        </StandardDialogActions>
      </StandardDialog>

      <StandardDialog
        title={'Reject Assessment?'}
        handleClose={handleRejectAssessmentClose}
        isOpen={rejectAssessmentOpen}
      >
        <Typography>Are you sure you want to reject this assessment?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={handleRejectAssessmentClose}>Cancel</Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleRejectAssessment}
            disabled={!userCanAdminCompany}
          >
            Reject
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      {canAdminAssessment && (
        <AssessmentForm
          assessment={cAssessment}
          modalOpen={assessmentModalOpen}
          company={company}
          closeCallback={() => setAssessmentModalOpen(false)}
        />
      )}
    </>
  );
};

export default AssessmentCard;
